import { useClient, usePortal, type AuditRow } from "@app/portal-sdk";
import { Alert, DataTable, EmptyState, PageHeader, Stack } from "@app/ui-kit";
import { AuditChanges } from "../../components/AuditChanges";
import { LoadingScreen } from "../../components/LoadingScreen";
import { displayName, formatDateTime } from "../../lib/format";
import { useApiQuery } from "../../lib/useApiQuery";

/**
 * CORE-1: the client's activity log, for a client administrator. The same
 * rows the admin console's Activity tab shows, with what changed rendered as
 * a sentence in the client's own words.
 */
const ACTION_LABELS: Record<string, string> = {
  "client.settings_update": "Changed settings",
  "branding.update": "Updated branding",
  "logo.update": "Updated logo",
  "logo.remove": "Removed logo",
  "member.invite": "Invited a member",
  "member.resend": "Resent an invitation",
  "member.update": "Updated a member",
  "member.activate": "Accepted an invitation",
  "app.update": "Changed application access",
  "file.upload": "Uploaded a file",
  "file.download": "Downloaded a file",
  "file.delete": "Deleted a file",
  "admin.enter_client": "BKDP opened the portal"
};
const actionLabel = (action: string): string => ACTION_LABELS[action] ?? action.replace(/^(app|core|ref)\./, "").replace(/[._]/g, " ");

export const Activity = () => {
  const { api } = usePortal();
  const { client } = useClient();
  const rows = useApiQuery<AuditRow[]>(() => api.request<AuditRow[]>(`/clients/${client.id}/audit?limit=100`), [api, client.id]);
  return (
    <Stack spacing={3}>
      <PageHeader eyebrow={client.name} title="Activity" subtitle="Who changed what, and when. Where a value changed, the entry says what it was before." />
      {rows.error && <Alert severity="error">{rows.error}</Alert>}
      {rows.loading && !rows.data ? <LoadingScreen minHeight={200} /> : (rows.data ?? []).length === 0 ? <EmptyState eyebrow="Activity" title="Nothing recorded yet" /> : (
        <DataTable
          columns={[{ key: "when", label: "When" }, { key: "who", label: "Who" }, { key: "action", label: "Action" }, { key: "target", label: "Target" }, { key: "changes", label: "What changed" }]}
          rows={(rows.data ?? []).map((row) => ({ when: formatDateTime(row.createdAt), who: displayName(row.actor), action: actionLabel(row.action), target: row.target ?? "—", changes: <AuditChanges row={row} /> }))}
          maxHeight={600}
          stickyHeader
        />
      )}
    </Stack>
  );
};
